<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { registerUser, confirmUser } from '@/lib/backend';
import SimpleSpoiler from '@/components/SimpleSpoiler.vue';

const EMET_LIVE_DAO_ADDRESS = '0xd5c2d8f9024799c1f0255371a6655f4c2bda584a';

const REGISTER_INVITE = 'invite';
const REGISTER_FORM = 'form';
const REGISTER_CONFIRM = 'confirm';
const REGISTER_SUCCESS = 'success';
const REGISTER_CONFIRMATION_ERROR = 'confirmation_error';
const REGISTER_CONFIRMATION_PENDING = 'register_confirmation_pending';

// i18n
const { t } = useI18n();

// refs
const showSpoiler = ref(false);
const registrationState = ref(REGISTER_INVITE);
const registrationInProgress = ref(false);
const registrationError = ref(null);
const confirmationError = ref(null);
const copyDone = ref(false);
const treasuryAddress = ref(null);
const user = ref({
  ethAddress: '',
  name: '',
  email: '',
  web: '',
});

// methods
function showRegistrationForm() {
  registrationState.value = REGISTER_FORM;
}

function isValidAddress(ethAddress) {
  ethAddress = ethAddress.toLowerCase();
  return /^0x[0-9a-f]{40}$/.test(ethAddress);
}

function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function preprocessForm() {
  user.value.ethAddress = user.value.ethAddress.trim();
  user.value.email = user.value.email.trim();
  user.value.name = user.value.name.trim();
  user.value.web = user.value.web.trim();
}

function validateForm() {
  if(user.value.ethAddress.length == 0) {
    throw t('empty_address');
  }
  if(!isValidAddress(user.value.ethAddress)) {
    throw t('invalid_address');
  }
  if(user.value.ethAddress.toLowerCase() == EMET_LIVE_DAO_ADDRESS) {
    throw t('cannot_use_dao_address');
  }
  if(user.value.name.length == 0) {
    throw t('empty_name');
  }
  if(user.value.email.length == 0) {
    throw t('empty_email');
  }
  if(!isValidEmail(user.value.email)) {
    throw t('invalid_email');
  }
}

async function register() {
  try {
    registrationInProgress.value = true;
    resetRegistrationError();
    preprocessForm();
    validateForm();
    const locale = window.AppConfig?.locale || 'en';
    await registerUser({...user.value, locale });
    registrationState.value = REGISTER_CONFIRM;
  } catch(error) {
    console.log(`Failed to register user: ${error}`);
    setRegistrationError(error);
  } finally {
    registrationInProgress.value = false;
  }
}

async function confirm(ethAddress, code) {
  try {
    registrationState.value = REGISTER_CONFIRMATION_PENDING;
    resetConfirmationError();
    await confirmUser({ ethAddress, code });
    registrationState.value = REGISTER_SUCCESS;
  } catch(error) {
    registrationState.value = REGISTER_CONFIRMATION_ERROR;
    console.log(`Failed to confirm user: ${error}`);
    setConfirmationError(error);
  }
}

function resetRegistrationError() {
  registrationError.value = null;
}

function setRegistrationError(message) {
  registrationError.value = message;
}

function resetConfirmationError() {
  confirmationError.value = null;
}

function setConfirmationError(message) {
  confirmationError.value = message;
}

function copyToClipboard(inputElement) {
  // Select the text inside the input element
  const range = document.createRange();
  range.selectNode(inputElement);
  window.getSelection().removeAllRanges();
  window.getSelection().addRange(range);

  // Copy the selected text to the clipboard
  document.execCommand('copy');

  // Clear selection
  window.getSelection().removeAllRanges();
}

function copyAddress() {
  copyToClipboard(treasuryAddress.value);

  // Show "copy done" icon for 2.5 seconds
  copyDone.value = true;
  setTimeout(() => copyDone.value = false, 2500);
}

// lifecycle hooks
onMounted(async () => {
  if(window.AppConfig?.confirmationEthAddress) {
    await confirm(window.AppConfig?.confirmationEthAddress, window.AppConfig?.confirmationCode);
  }
});
</script>

<template>
  <div class="page">

    <div v-if="registrationState == REGISTER_CONFIRMATION_PENDING">
      <div class="info-text">
        {{ $t('confirmation_pending') }}
      </div>
    </div>
    <div v-else-if="registrationState == REGISTER_SUCCESS">
      <div class="info-text info-text-strong">
        {{ $t('registration_success') }}
      </div>
    </div>
    <div v-else-if="confirmationError" class="form-error">
      {{ confirmationError }}
    </div>

    <div class="info-container">
      <div class="info-text">
        {{ $t('welcome_1') }}
      </div>
      <div class="info-text">
        {{ $t('welcome_2') }}
      </div>

      <div class="treasury-block-container">
        <div class="info-text info-text-strong">
          {{ $t('wallet_address') }}
        </div>

        <div class="treasury-address">
          <div class="treasury-address-container">
            <span
              ref="treasuryAddress"
              class="treasury-address-value"
              @click="copyAddress"
              :class="{'treasury-address-highlight': copyDone }"
              >
              0xD5c2D8f9024799C1f0255371A6655f4C2bda584a
            </span>

            <span
              v-if="copyDone"
              class="treasury-address-icon material-symbols-outlined"
              >
              done_all
            </span>
            <span
              v-else
              @click="copyAddress"
              class="treasury-address-icon material-symbols-outlined"
              >
              content_copy
            </span>

          </div>
        </div>
      </div>

      <simple-spoiler
        v-if="showSpoiler"
        :title="$t('other_options_to_donate')"
        >
        Spoiler content
      </simple-spoiler>

      <div class="info-text info-text-secondary">
        {{ $t('text_verify') }}
        <a href="https://app.aragon.org/#/daos/ethereum/emetlive.dao.eth/dashboard" target="_blank">Emet Live DAO Dashboard</a>
      </div>
      <div class="info-text info-text-secondary">
        {{ $t('text_instructions') }}
        <a href="https://app.aragon.org/#/daos/ethereum/emetlive.dao.eth/finance/transfers" target="_blank">Emet Live DAO Finance Transfers</a>
      </div>

    </div>

    <div class="register-invitation" v-if="registrationState == REGISTER_INVITE">
      <div class="info-text-big-title info-text">
        {{ $t('registration_welcome_title') }}
      </div>
      <div class="info-text">
        {{ $t('welcome_3') }}
      </div>
      <div class="info-text">
        {{ $t('registration_welcome_text') }}
      </div>
      <div class="info-text info-text-centered">
        <button class="button-transparent" @click="showRegistrationForm">
          {{ $t('register') }}
        </button>
      </div>
    </div>

    <div class="registration-form" v-if="registrationState == REGISTER_FORM">
      <div class="info-text info-text-big-title">
        {{ $t('registration') }}
      </div>

      <div class="form-inputs">
        <input type="text" class="text-input" v-model="user.ethAddress" :placeholder="$t('your_ethereum_address') + ' (0x...)'" />
        <input type="text" class="text-input" v-model="user.name" :placeholder="$t('name')" />
        <input type="text" class="text-input" v-model="user.email" :placeholder="$t('email')" />
        <input type="text" class="text-input" v-model="user.web" :placeholder="$t('web')" />
      </div>

      <div v-if="registrationError" class="form-error">
        {{ registrationError }}
      </div>

      <button class="button-transparent" @click="register" :disabled="registrationInProgress">
        {{ $t('register') }}
      </button>
    </div>

    <div class="registration-form" v-if="registrationState == REGISTER_CONFIRM">
      <div class="info-text info-text-big-title">
        {{ $t('registration') }}
      </div>
      <div class="info-text">
        {{ $t('registration_confirm', { mailFrom: 'noreply@emet.live' } ) }}
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
$buttonBorderColor: #ccc;
$buttonHoverColor: #f0f0f0;

input, button {
  font-size: 100%;
  font-family: inherit;
  font-weight: inherit;
}
.page {
  text-align: start;
}
.info-text {
  margin-top: 16px;
  letter-spacing: var(--info-text-letter-spacing-desktop);
  line-height: var(--info-text-line-height-desktop);
}
.info-text-strong {
  font-weight: var(--info-text-subtitle-weight);
}
.info-text-title {
  margin-top: 24px;
  font-size: 120%;
  font-weight: 600;
}
.info-text-big-title {
  font-size: 46px;
  letter-spacing: .05em;
  line-height: 1.25;
  font-weight: var(--font-weight-title-text);
  font-family: var(--font-family-title);
}
.info-text-secondary {
  font-style: italic;
  font-size: 80%;
}
.info-text-centered {
  text-align: center;
}
.treasury-block-container {
  text-align: center;
}
.treasury-address {
  display: inline-block;
  margin: 8px 0px;


  .treasury-address-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .treasury-address-value {
    margin-right: 8px;
    padding: 8px;
    border: 1px solid $buttonBorderColor;
  }

  .treasury-address-highlight {
    background-color: #eee;
  }

  .treasury-address-icon {
    cursor: pointer;
    font-size: 125%;
    color: rgba(0, 0, 0, .5);

    &:hover {
      color: rgba(0, 0, 0, 1);
    }
  }
}

.register-invitation {
  margin-top: 32px;
  text-align: center;
}
.invitation-text {
  margin-bottom: 8px;
}
.registration-form {
  margin-top: 32px;
  text-align: center;
}
.registration-form input {
  display: block;
  margin: 8px auto;
  width: 360px;
}
.button-transparent {
  background-color: transparent;
  border: 1px solid $buttonBorderColor;
  padding: 8px 16px;
  cursor: pointer;
}
.button-transparent:hover {
  background-color: $buttonHoverColor;
}
.text-input {
  background-color: transparent;
  border: 1px solid $buttonBorderColor;
  padding: 8px 16px;
}
.form-error {
  margin: 16px 0;
  color: #a00;
}

@media screen and (max-width:767px) {
  .page {
    text-align: start;
  }
  .info-text {
    letter-spacing: var(--info-text-letter-spacing-mobile);
    line-height: var(--info-text-line-height-mobile);
  }
  .register-invitation {
    text-align: start;
  }
  .info-text-big-title {
    font-size: 7.3vw;
    line-height: 1.2;
  }
  .registration-form input {
    width: 80vw;
  }
  .treasury-address {
    font-size: 2.5vw;
  }
  .registration-form input {
    display: block;
    margin: 8px 0;
    width: 80vw;
  }
  .treasury-address-icon {
    display: none;
  }
}
</style>